import React from "react"
import { Platform, StyleSheet, View } from "react-native"
import {
  SafeAreaView,
  useSafeAreaInsets,
} from "react-native-safe-area-context"
import { useStyle } from "react-native-style-utilities"

import { checkPermissions } from "@/base/check-permissions"
import useHeaderSize from "@/hooks/useHeaderSize"
import { useRootSelector } from "@/redux/useRootSelector"
import { COLORS, testIds, useIsDarkMode } from "@/theme"

import { PermissionDeniedFallback } from "./PermissionDeniedFallback"

import { FocusTrackingStatusBar } from "@/components/FocusTrackingStatusBar"

import type { RequiredPermissions } from "@/constants/permissions"
import { getIsAdminModeEnabledFromState } from "@/redux/selectors"
import type { FeatureId } from "@/theme"
import type { StatusBarStyle } from "expo-status-bar"
import type { Edge, SafeAreaViewProps } from "react-native-safe-area-context"

const EDGES_DEFAULT: Edge[] = ["left", "right"]
interface StaticOptions {
  adminOnly?: boolean
  devOnly?: boolean
  edges?: Edge[]
  headerLarge?: boolean
  id: FeatureId
  requiredPermissions?: RequiredPermissions
  statusBarStyle?: StatusBarStyle
  variant?: "paper"
}

type ScreenLayoutProps = SafeAreaViewProps & StaticOptions

export function ScreenLayout({
  adminOnly,
  children,
  devOnly,
  edges,
  headerLarge,
  id,
  requiredPermissions,
  statusBarStyle,
  variant,
  ...props
}: ScreenLayoutProps) {
  const isDark = useIsDarkMode()
  const headerHeight = useHeaderSize()
  const insetTop = useSafeAreaInsets().top
  const hasPermission = useRootSelector((state) => {
    if (Boolean(requiredPermissions)) {
      const check = checkPermissions({
        provided: state.userPermissions.farmUserPermissions,
        required: requiredPermissions,
      })
      if (check.code === "MISSING_PERMISSIONS") {
        return false
      }
    }
    if (adminOnly === true && !getIsAdminModeEnabledFromState(state)) {
      return false
    }
    if (devOnly === true && !__DEV__) {
      return false
    }
    return true
  })

  // React.useEffect(() => {
  //   logger.info(`SCREEN: ${id}`)
  // }, [id])
  return (
    <SafeAreaView
      {...testIds(id)}
      edges={edges ?? EDGES_DEFAULT}
      style={useStyle(
        () => [
          styles.root,
          styles.background,
          isDark && styles.backgroundDark,
          headerLarge === true &&
            Platform.OS === "ios" && {
              paddingTop: headerHeight + insetTop,
            },
          variant === "paper" && styles.variantPaper,
          variant === "paper" && isDark && styles.variantPaperDark,
        ],
        [headerHeight, headerLarge, insetTop, isDark, variant],
      )}
      {...props}
    >
      <FocusTrackingStatusBar style={statusBarStyle} />
      {hasPermission ? (
        children
      ) : (
        <View style={styles.permissionDeniedContainer}>
          <PermissionDeniedFallback />
        </View>
      )}
    </SafeAreaView>
  )
}

const styles = StyleSheet.create({
  background: {
    backgroundColor: COLORS.$background.light,
  },
  backgroundDark: {
    backgroundColor: COLORS.$background.dark,
  },
  permissionDeniedContainer: {
    flex: 1,
    margin: "auto",
    maxHeight: 500,
  },
  root: {
    flex: 1,
    position: "relative",
  },
  variantPaper: {
    backgroundColor: COLORS.$paper.light,
  },
  variantPaperDark: {
    backgroundColor: COLORS.$paper.dark,
  },
})

export function withScreenLayout<P extends React.JSX.IntrinsicAttributes>(
  Component: React.ComponentType<P>,
  options: StaticOptions,
) {
  function Wrapped(props: P) {
    return (
      <ScreenLayout {...options}>
        <Component {...props} />
      </ScreenLayout>
    )
  }
  Wrapped.displayName = Component.displayName

  return Wrapped
}
